/* ==========================================================================
   GLOBAL FRESH CONNECT — behaviour layer
   Vanilla, dependency-free, progressive enhancement only.
   Everything degrades to a usable static page if JS never runs.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * 1. Scroll reveal
   * ------------------------------------------------------------------ */
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-gfc-reveal]:not(.is-in)');
    if (!nodes.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* Stagger children of any [data-gfc-stagger] container. */
  function initStagger(root) {
    (root || document).querySelectorAll('[data-gfc-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-gfc-stagger'), 10) || 80;
      Array.prototype.forEach.call(group.children, function (child, i) {
        if (child.hasAttribute('data-gfc-reveal')) {
          child.style.setProperty('--gfc-delay', i * step + 'ms');
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Sticky header shadow
   * ------------------------------------------------------------------ */
  function initHeader(root) {
    (root || document).querySelectorAll('[data-gfc-header]').forEach(function (header) {
      if (header.__gfcBound) return;
      header.__gfcBound = true;

      /* Anchor the sentinel above the whole section. Placing it inside the
         header meant it stuck along with the header and never left the
         viewport, so the stuck state never fired. */
      var anchor = header.closest('.gfc-header-section') || header.parentNode;
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;';
      anchor.parentNode.insertBefore(sentinel, anchor);

      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        function (entries) {
          header.classList.toggle('is-stuck', !entries[0].isIntersecting);
        },
        { threshold: 0 }
      ).observe(sentinel);
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Mobile drawer
   * ------------------------------------------------------------------ */
  function initDrawer(root) {
    (root || document).querySelectorAll('[data-gfc-drawer]').forEach(function (drawer) {
      if (drawer.__gfcBound) return;
      drawer.__gfcBound = true;

      var id = drawer.id;
      var openers = document.querySelectorAll('[data-gfc-drawer-open="' + id + '"]');
      var closers = drawer.querySelectorAll('[data-gfc-drawer-close]');
      var panel = drawer.querySelector('.gfc-drawer__panel');
      var lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        var first = panel && panel.querySelector('a,button,input,select,textarea');
        if (first) first.focus({ preventScroll: true });
      }

      function close() {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        if (lastFocus) lastFocus.focus({ preventScroll: true });
      }

      openers.forEach(function (b) { b.addEventListener('click', open); });
      closers.forEach(function (b) { b.addEventListener('click', close); });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });

      /* Collapsible sub-menus inside the drawer. */
      drawer.querySelectorAll('[data-gfc-drawer-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = document.getElementById(btn.getAttribute('aria-controls'));
          if (!target) return;
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          target.hidden = open;
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Map arcs — measure path length so the draw-on animation is exact
   * ------------------------------------------------------------------ */
  function initArcs(root) {
    (root || document).querySelectorAll('.gfc-map__arc').forEach(function (path) {
      if (path.__gfcBound || typeof path.getTotalLength !== 'function') return;
      path.__gfcBound = true;
      try {
        var len = Math.ceil(path.getTotalLength());
        path.style.setProperty('--gfc-arc-len', len);
      } catch (e) {
        /* Detached or zero-length path — leave the CSS fallback in place. */
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. RFQ form — stepper, dropzone, section tracking
   * ------------------------------------------------------------------ */
  function initRfq(root) {
    (root || document).querySelectorAll('[data-gfc-rfq]').forEach(function (form) {
      if (form.__gfcBound) return;
      form.__gfcBound = true;

      var steps = Array.prototype.slice.call(form.querySelectorAll('[data-gfc-step]'));
      var dots = Array.prototype.slice.call(form.querySelectorAll('[data-gfc-step-to]'));

      /* Clicking a stepper dot scrolls to its fieldset. */
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var target = form.querySelector('[data-gfc-step="' + dot.getAttribute('data-gfc-step-to') + '"]');
          if (target) target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
        });
      });

      /* Highlight the stepper dot for whichever fieldset is in view. */
      if (steps.length && dots.length && 'IntersectionObserver' in window) {
        var spy = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var key = entry.target.getAttribute('data-gfc-step');
              var reached = true;
              dots.forEach(function (dot) {
                var isCurrent = dot.getAttribute('data-gfc-step-to') === key;
                dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
                dot.classList.toggle('is-done', reached && !isCurrent);
                if (isCurrent) reached = false;
              });
            });
          },
          { rootMargin: '-45% 0px -45% 0px' }
        );
        steps.forEach(function (s) { spy.observe(s); });
      }

      /* Dropzone: Shopify's contact form cannot carry a file, so we record
         the filename in a hidden field and tell the buyer we'll request it. */
      var drop = form.querySelector('[data-gfc-drop]');
      if (drop) {
        var input = drop.querySelector('input[type="file"]');
        var out = drop.querySelector('[data-gfc-drop-file]');
        var mirror = form.querySelector('[data-gfc-drop-mirror]');

        var show = function (files) {
          if (!files || !files.length) return;
          var names = Array.prototype.map.call(files, function (f) { return f.name; }).join(', ');
          if (out) out.textContent = names;
          if (mirror) mirror.value = names;
        };

        input.addEventListener('change', function () { show(input.files); });

        ['dragenter', 'dragover'].forEach(function (evt) {
          drop.addEventListener(evt, function (e) {
            e.preventDefault();
            drop.classList.add('is-drag');
          });
        });
        ['dragleave', 'drop'].forEach(function (evt) {
          drop.addEventListener(evt, function (e) {
            e.preventDefault();
            drop.classList.remove('is-drag');
          });
        });
        drop.addEventListener('drop', function (e) {
          if (e.dataTransfer && e.dataTransfer.files.length) {
            try { input.files = e.dataTransfer.files; } catch (err) { /* read-only in some browsers */ }
            show(e.dataTransfer.files);
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 5b. Tone engine
   * A struck-string voice built from three partials with a fast attack
   * and a long exponential decay. Twelve notes of a C major pentatonic
   * climb across two octaves, so playing the supply chain start to
   * finish reads as an ascending phrase rather than twelve random beeps.
   * ------------------------------------------------------------------ */
  var GfcTone = (function () {
    var ctx = null;
    var SCALE = [
      261.63, 293.66, 329.63, 392.0, 440.0, 523.25,
      587.33, 659.25, 783.99, 880.0, 1046.5, 1174.66,
    ];

    /* Browsers only allow audio to start from a user gesture, which is
       exactly why the wheel waits for a press rather than autoplaying. */
    function unlock() {
      if (!ctx) {
        var Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        try { ctx = new Ctor(); } catch (e) { return null; }
      }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }

    function note(index, gainScale) {
      var ac = unlock();
      if (!ac) return;
      var freq = SCALE[index % SCALE.length];
      var t = ac.currentTime;
      var peak = 0.15 * (gainScale || 1);

      var out = ac.createGain();
      out.gain.setValueAtTime(0.0001, t);
      out.gain.exponentialRampToValueAtTime(peak, t + 0.012);
      out.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
      out.connect(ac.destination);

      [[1, 1, 'triangle'], [2, 0.3, 'sine'], [3.01, 0.1, 'sine']].forEach(function (p) {
        var osc = ac.createOscillator();
        osc.type = p[2];
        osc.frequency.setValueAtTime(freq * p[0], t);
        var g = ac.createGain();
        g.gain.value = p[1];
        osc.connect(g);
        g.connect(out);
        osc.start(t);
        osc.stop(t + 2);
      });
    }

    /* Browsers refuse to start audio without a gesture, and the wheel now
       plays on its own. Arm the context on the first interaction anywhere on
       the page so the tones join in as soon as they legally can. */
    function armOnFirstGesture() {
      var events = ['pointerdown', 'keydown', 'touchstart'];
      function once() {
        unlock();
        events.forEach(function (e) { document.removeEventListener(e, once); });
      }
      events.forEach(function (e) { document.addEventListener(e, once, { once: true, passive: true }); });
    }

    return { note: note, unlock: unlock, armOnFirstGesture: armOnFirstGesture };
  })();

  GfcTone.armOnFirstGesture();

  /* ------------------------------------------------------------------ *
   * 6. Supply wheel
   * ------------------------------------------------------------------ */
  function initWheel(root) {
    (root || document).querySelectorAll('[data-gfc-wheel]').forEach(function (wrap) {
      if (wrap.__gfcBound) return;
      wrap.__gfcBound = true;

      var wheel = wrap.querySelector('.gfc-wheel');
      var rotor = wrap.querySelector('[data-gfc-wheel-rotor]');
      var segs = Array.prototype.slice.call(wrap.querySelectorAll('[data-gfc-seg]'));
      var detail = wrap.querySelector('[data-gfc-wheel-detail]');
      var counter = wrap.querySelector('[data-gfc-wheel-count]');
      var prev = wrap.querySelector('[data-gfc-wheel-prev]');
      var next = wrap.querySelector('[data-gfc-wheel-next]');
      if (!segs.length) return;

      var total = segs.length;
      var stepAngle = 360 / total;
      var index = 0;
      var timer = null;
      var autoplay = wrap.getAttribute('data-gfc-wheel') === 'auto';
      var playBtn = wrap.querySelector('[data-gfc-wheel-play]');
      var soundBtn = wrap.querySelector('[data-gfc-wheel-sound]');
      var soundOn = wrap.getAttribute('data-gfc-wheel-sound-on') !== '0';

      /* Stage numbers sit at the centre of each arc. Liquid has no
         trigonometry, so the positions are placed here instead. */
      (function drawNumbers() {
        var band = wrap.querySelector('[data-gfc-wheel-band]');
        if (!band || band.querySelector('.gfc-wheel__seg-num')) return;
        var NS = 'http://www.w3.org/2000/svg';
        var group = document.createElementNS(NS, 'g');
        group.setAttribute('transform', 'rotate(90 250 250)');
        group.setAttribute('aria-hidden', 'true');

        segs.forEach(function (seg, i) {
          var deg = -90 + (i + 0.5) * stepAngle;
          var rad = (deg * Math.PI) / 180;
          var text = document.createElementNS(NS, 'text');
          text.setAttribute('class', 'gfc-wheel__seg-num');
          text.setAttribute('x', (250 + 180 * Math.cos(rad)).toFixed(1));
          text.setAttribute('y', (250 + 180 * Math.sin(rad) + 5).toFixed(1));
          text.setAttribute('text-anchor', 'middle');
          text.textContent = seg.dataset.num || String(i + 1);
          group.appendChild(text);
        });

        band.appendChild(group);
      })();

      function strike(i) {
        if (!soundOn) return;
        GfcTone.note(i);
        var seg = segs[i];
        seg.classList.remove('is-struck');
        void seg.offsetWidth; /* restart the animation */
        seg.classList.add('is-struck');
      }

      function render(i, userDriven, silent) {
        index = ((i % total) + total) % total;
        var seg = segs[index];

        wheel.classList.remove('is-idle');
        segs.forEach(function (s, n) { s.classList.toggle('is-active', n === index); });
        if (!silent) strike(index);

        /* Bring the active segment to the top of the dial. */
        if (rotor && !REDUCED) {
          rotor.style.transform = 'rotate(' + -index * stepAngle + 'deg)';
        }

        if (detail) {
          detail.innerHTML =
            '<div class="gfc-wheel__detail-body">' +
            '<p class="gfc-wheel__detail-num">' + (seg.dataset.num || '') + '</p>' +
            '<h3 class="gfc-h3 gfc-wheel__detail-title">' + (seg.dataset.title || '') + '</h3>' +
            '<p class="gfc-lead gfc-wheel__detail-text">' + (seg.dataset.text || '') + '</p>' +
            '</div>';
        }
        if (counter) counter.textContent = index + 1 + ' / ' + total;
        if (userDriven && !autoplay) stop();
      }

      function start() {
        if (timer) return;
        wheel.classList.add('is-playing');
        if (playBtn) playBtn.setAttribute('aria-pressed', 'true');
        timer = window.setInterval(function () { render(index + 1); }, 1500);
      }
      function stop() {
        if (timer) { window.clearInterval(timer); timer = null; }
        wheel.classList.remove('is-playing');
        if (playBtn) playBtn.setAttribute('aria-pressed', 'false');
      }
      function toggle() {
        if (timer) { stop(); return; }
        GfcTone.unlock();
        /* Restart from the top once the sequence has run its course. */
        if (index >= total - 1) { render(0, false, true); }
        strike(index);
        start();
      }

      segs.forEach(function (seg, n) {
        seg.addEventListener('click', function () { GfcTone.unlock(); render(n, true); });
        seg.addEventListener('focus', function () { render(n, true); });
        seg.setAttribute('tabindex', '0');
        seg.setAttribute('role', 'button');
        seg.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); render(n, true); }
        });
      });

      if (prev) prev.addEventListener('click', function () { render(index - 1, true); });
      if (next) next.addEventListener('click', function () { render(index + 1, true); });

      if (playBtn) playBtn.addEventListener('click', toggle);

      if (soundBtn) {
        soundBtn.setAttribute('aria-pressed', String(soundOn));
        soundBtn.addEventListener('click', function () {
          soundOn = !soundOn;
          soundBtn.setAttribute('aria-pressed', String(soundOn));
          soundBtn.setAttribute('aria-label', soundOn ? 'Mute the stage tones' : 'Unmute the stage tones');
          if (soundOn) GfcTone.unlock();
        });
      }

      /* Opening state: first stage shown, nothing sounding. */
      render(0, false, true);
      if (autoplay) wheel.classList.add("is-auto");

      /* Stop when the wheel leaves the screen — no sound from off-screen. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) stop();
            else if (autoplay && !timer) start();
          },
          { threshold: 0.25 }
        ).observe(wrap);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 7. Marquee — duplicate the track so the loop is seamless
   * ------------------------------------------------------------------ */
  function initMarquee(root) {
    (root || document).querySelectorAll('[data-gfc-marquee]').forEach(function (el) {
      if (el.__gfcBound) return;
      el.__gfcBound = true;
      var track = el.querySelector('.gfc-marquee__track');
      if (!track) return;
      track.innerHTML += track.innerHTML;
    });
  }

  /* ------------------------------------------------------------------ *
   * 8. FAQ — keep one answer open per group when asked to
   * ------------------------------------------------------------------ */
  function initFaq(root) {
    (root || document).querySelectorAll('[data-gfc-faq-exclusive]').forEach(function (group) {
      if (group.__gfcBound) return;
      group.__gfcBound = true;
      var items = Array.prototype.slice.call(group.querySelectorAll('details'));
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) { if (other !== item) other.open = false; });
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 8b. Header search panel
   * ------------------------------------------------------------------ */
  function initSearch(root) {
    (root || document).querySelectorAll('[data-gfc-search-panel]').forEach(function (panel) {
      if (panel.__gfcBound) return;
      panel.__gfcBound = true;

      var openers = document.querySelectorAll('[data-gfc-search-open][aria-controls="' + panel.id + '"]');
      var closers = panel.querySelectorAll('[data-gfc-search-close]');
      var input = panel.querySelector('input[name="q"]');

      function open() {
        panel.hidden = false;
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        if (input) input.focus();
      }
      function close() {
        panel.hidden = true;
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      }

      openers.forEach(function (b) {
        b.addEventListener('click', function () { panel.hidden ? open() : close(); });
      });
      closers.forEach(function (b) { b.addEventListener('click', close); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !panel.hidden) close();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 9. Footer columns — accordion on small screens, plain lists above
   * ------------------------------------------------------------------ */
  function initFooter(root) {
    var toggles = (root || document).querySelectorAll('[data-gfc-footer-toggle]');
    if (!toggles.length) return;

    var mq = window.matchMedia('(max-width: 749px)');

    function apply() {
      toggles.forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        if (mq.matches) {
          /* Collapse on entry so the footer is short on a phone. */
          if (!btn.__gfcTouched) { btn.setAttribute('aria-expanded', 'false'); panel.hidden = true; }
        } else {
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    }

    toggles.forEach(function (btn) {
      if (btn.__gfcBound) return;
      btn.__gfcBound = true;
      btn.addEventListener('click', function () {
        if (!mq.matches) return;
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        btn.__gfcTouched = true;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    apply();
    if (mq.addEventListener) mq.addEventListener('change', function () {
      toggles.forEach(function (b) { b.__gfcTouched = false; });
      apply();
    });
  }

  /* ------------------------------------------------------------------ *
   * 10. SEO topics — accordion on small screens, open list above
   * ------------------------------------------------------------------ */
  function initSeo(root) {
    var toggles = (root || document).querySelectorAll('[data-gfc-seo-toggle]');
    if (!toggles.length) return;
    var mq = window.matchMedia('(max-width: 749px)');

    function apply() {
      toggles.forEach(function (btn, i) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        if (mq.matches) {
          if (!btn.__gfcTouched) {
            /* Leave the first topic open so the band never reads as empty. */
            var open = i === 0;
            btn.setAttribute('aria-expanded', String(open));
            panel.hidden = !open;
          }
        } else {
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    }

    toggles.forEach(function (btn) {
      if (btn.__gfcBound) return;
      btn.__gfcBound = true;
      btn.addEventListener('click', function () {
        if (!mq.matches) return;
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        btn.__gfcTouched = true;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    apply();
    if (mq.addEventListener) mq.addEventListener('change', function () {
      toggles.forEach(function (b) { b.__gfcTouched = false; });
      apply();
    });
  }

  /* ------------------------------------------------------------------ *
   * 11. Left scroll rail — progress, next section, back to top
   * ------------------------------------------------------------------ */
  function initRail(root) {
    var rail = (root || document).querySelector('[data-gfc-rail]');
    if (!rail || rail.__gfcBound) return;
    rail.__gfcBound = true;

    var fill = rail.querySelector('[data-gfc-rail-fill]');
    var btn = rail.querySelector('[data-gfc-rail-btn]');
    var atEnd = false;

    function sections() {
      return Array.prototype.filter.call(
        document.querySelectorAll('main section, main > div > section, .shopify-section > section'),
        function (el) { return el.offsetHeight > 120; }
      );
    }

    /* Keep the rail legible over whichever band sits behind it. */
    function tone() {
      var y = window.innerHeight / 2;
      var el = document.elementFromPoint(4, y);
      var dark = false;
      while (el && el !== document.body) {
        if (el.classList && (el.classList.contains('gfc-on-dark') ||
            el.classList.contains('gfc-section--navy') ||
            el.classList.contains('gfc-section--navy-deep') ||
            el.classList.contains('gfc-quick') ||
            el.classList.contains('gfc-footer--navy'))) { dark = true; break; }
        el = el.parentElement;
      }
      rail.classList.toggle('is-on-dark', dark);
    }

    function update() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var pct = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (fill) fill.style.height = (pct * 100).toFixed(1) + '%';

      atEnd = pct > 0.92;
      rail.classList.toggle('is-end', atEnd);
      if (btn) btn.setAttribute('aria-label', atEnd ? 'Back to top' : 'Scroll to the next section');
      tone();
    }

    if (btn) {
      btn.addEventListener('click', function () {
        var behavior = REDUCED ? 'auto' : 'smooth';
        if (atEnd) { window.scrollTo({ top: 0, behavior: behavior }); return; }
        var y = window.scrollY + 10;
        var next = sections().find(function (el) {
          return el.getBoundingClientRect().top + window.scrollY > y + 10;
        });
        if (next) next.scrollIntoView({ behavior: behavior, block: 'start' });
        else window.scrollTo({ top: document.documentElement.scrollHeight, behavior: behavior });
      });
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------ *
   * 12. WhatsApp assistant
   * A scripted qualifier, not a language model. It gathers the answers a
   * sourcing conversation always needs and hands them to WhatsApp so the
   * first human reply is already informed.
   * ------------------------------------------------------------------ */
  function initWhatsApp(root) {
    (root || document).querySelectorAll('[data-gfc-wa]').forEach(function (wrap) {
      if (wrap.__gfcBound) return;
      wrap.__gfcBound = true;

      var panel = wrap.querySelector('[data-gfc-wa-panel]');
      var thread = wrap.querySelector('[data-gfc-wa-thread]');
      var choices = wrap.querySelector('[data-gfc-wa-choices]');
      var send = wrap.querySelector('[data-gfc-wa-send]');
      var opener = wrap.querySelector('[data-gfc-wa-open]');
      var closer = wrap.querySelector('[data-gfc-wa-close]');
      var badge = wrap.querySelector('.gfc-wa__badge');
      var dataEl = wrap.querySelector('[data-gfc-wa-script]');

      var phone = wrap.getAttribute('data-wa-phone') || '';
      var intro = wrap.getAttribute('data-wa-intro') || '';
      var topics = [];
      try { topics = (JSON.parse(dataEl.textContent) || {}).topics || []; } catch (e) { topics = []; }

      var started = false;

      function bubble(text, who) {
        var el = document.createElement('div');
        el.className = 'gfc-wa__msg gfc-wa__msg--' + who;
        el.textContent = text;
        thread.appendChild(el);
        thread.scrollTop = thread.scrollHeight;
        return el;
      }

      function typing(then) {
        var el = document.createElement('div');
        el.className = 'gfc-wa__msg gfc-wa__msg--in gfc-wa__typing';
        el.innerHTML = '<span></span><span></span><span></span>';
        thread.appendChild(el);
        thread.scrollTop = thread.scrollHeight;
        window.setTimeout(function () { el.remove(); then(); }, REDUCED ? 0 : 650);
      }

      function chips(list, onPick) {
        choices.innerHTML = '';
        list.forEach(function (item, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'gfc-wa__chip';
          b.textContent = item.label;
          b.addEventListener('click', function () { onPick(item, i); });
          choices.appendChild(b);
        });
      }

      function askTopic(topic) {
        choices.innerHTML = '';
        var rows = (topic.fields || '').split('\n').map(function (r) { return r.trim(); }).filter(Boolean);
        if (!rows.length) { handoff(topic, []); return; }

        var form = document.createElement('form');
        form.className = 'gfc-wa__form';
        var inputs = [];

        rows.forEach(function (row) {
          var parts = row.split('|');
          var label = (parts[0] || '').trim();
          var ph = (parts[1] || '').trim();
          var wrapEl = document.createElement('label');
          wrapEl.className = 'gfc-wa__field';
          var span = document.createElement('span');
          span.textContent = label;
          var input = document.createElement('input');
          input.type = 'text';
          input.placeholder = ph;
          input.required = true;
          wrapEl.appendChild(span);
          wrapEl.appendChild(input);
          form.appendChild(wrapEl);
          inputs.push({ label: label, input: input });
        });

        var go = document.createElement('button');
        go.type = 'submit';
        go.className = 'gfc-wa__chip gfc-wa__chip--go';
        go.textContent = 'Done';
        form.appendChild(go);

        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var answers = inputs.map(function (f) { return { label: f.label, value: f.input.value.trim() }; });
          bubble(answers.map(function (a) { return a.label + ': ' + a.value; }).join('\n'), 'out');
          choices.innerHTML = '';
          typing(function () {
            bubble('Perfect — that is everything the team needs. Press the button below and it will open WhatsApp with this already written out.', 'in');
            handoff(topic, answers);
          });
        });

        choices.appendChild(form);
        if (inputs[0]) inputs[0].input.focus();
      }

      function handoff(topic, answers) {
        var lines = ['Hi Global Fresh Connect — ' + (topic.subject || 'Enquiry')];
        answers.forEach(function (a) { lines.push(a.label + ': ' + a.value); });
        var text = encodeURIComponent(lines.join('\n'));
        send.href = 'https://wa.me/' + phone + '?text=' + text;
        send.hidden = false;
        send.scrollIntoView({ block: 'nearest' });
      }

      function start() {
        if (started) return;
        started = true;
        if (badge) badge.remove();
        typing(function () {
          bubble(intro, 'in');
          chips(topics, function (topic) {
            bubble(topic.label, 'out');
            send.hidden = true;
            typing(function () {
              bubble(topic.reply, 'in');
              askTopic(topic);
            });
          });
        });
      }

      function open() {
        panel.hidden = false;
        wrap.classList.add('is-open');
        opener.setAttribute('aria-expanded', 'true');
        start();
      }
      function close() {
        panel.hidden = true;
        wrap.classList.remove('is-open');
        opener.setAttribute('aria-expanded', 'false');
      }

      opener.addEventListener('click', function () { panel.hidden ? open() : close(); });
      if (closer) closer.addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !panel.hidden) close();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot(root) {
    initFooter(root);
    initSearch(root);
    initSeo(root);
    initRail(root);
    initWhatsApp(root);
    initStagger(root);
    initReveal(root);
    initHeader(root);
    initDrawer(root);
    initArcs(root);
    initRfq(root);
    initWheel(root);
    initMarquee(root);
    initFaq(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Re-run for sections re-rendered by the Shopify theme editor. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
